import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook } from 'lucide-react';
import api from '../services/api';

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.52a8.27 8.27 0 0 0 4.85 1.56V6.69h-1.09z"/>
  </svg>
);

const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', Icon: Instagram, hoverBg: '#E4405F' },
  { key: 'facebook', label: 'Facebook', Icon: Facebook, hoverBg: '#1877F2' },
  { key: 'tiktok', label: 'TikTok', Icon: TikTokIcon, hoverBg: '#000000' },
];

// Fallback footer categories used if the API call fails.
const FALLBACK_FOOTER_CATEGORIES = [
  { id: 'mugg', name: 'Muggar' },
  { id: 'tshirt', name: 'T-shirts' },
  { id: 'hoodie', name: 'Hoodies' },
  { id: 'poster', name: 'Posters' },
  { id: 'tygkasse', name: 'Tygkassar' },
];

const Footer = () => {
  const [socialLinks, setSocialLinks] = useState({});
  const [categories, setCategories] = useState(FALLBACK_FOOTER_CATEGORIES);

  useEffect(() => {
    api.get('/site-settings')
      .then((res) => setSocialLinks(res.data?.social_links || {}))
      .catch((err) => console.debug('[footer] settings fetch failed:', err?.message));

    api.get('/products/categories')
      .then((res) => {
        const list = (res.data || [])
          .filter((c) => !c.hidden_in_footer)
          .map((c) => ({ id: c.id, name: c.name || c.id }));
        if (list.length > 0) setCategories(list);
        else setCategories([]);  // admin has explicitly hidden all — respect it
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  const visibleSocials = SOCIAL_PLATFORMS.filter((p) => (socialLinks[p.key] || '').trim());

  return (
    <footer className="bg-slate-50 border-t border-slate-100" data-testid="footer">
      <div className="container-main py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <Link to="/" className="block">
              <img 
                src="/logo.jpg" 
                alt="Printsout" 
                className="h-16 w-auto"
              />
            </Link>
            <p className="text-slate-500 text-sm leading-relaxed">
              Org-nr 940719-1437<br />
              PrintsOut<br />
              Lodjursvägen<br />
              686 33 Sunne<br />
              <a href="mailto:info@printsout.se" className="hover:text-primary transition-colors">info@printsout.se</a>
            </p>
            {visibleSocials.length > 0 && (
              <div className="flex items-center gap-3" data-testid="social-media-icons">
                {visibleSocials.map(({ key, label, Icon, hoverBg }) => (
                  <a
                    key={key}
                    href={socialLinks[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-slate-500 hover:text-white transition-all"
                    style={{ '--hover-bg': hoverBg }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverBg; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                    data-testid={`social-${key}`}
                    aria-label={label}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Products */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-4">Produkter</h4>
            {categories.length === 0 ? (
              <p className="text-slate-400 text-sm italic">Inga kategorier</p>
            ) : (
              <ul className="space-y-2">
                {categories.map((cat) => (
                  <li key={cat.id}>
                    <Link to={`/produkter/${cat.id}`} className="text-slate-500 hover:text-primary text-sm transition-colors">
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-4">Support</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/sida/faq" className="text-slate-500 hover:text-primary text-sm transition-colors">FAQ</Link>
              </li>
              <li>
                <Link to="/sida/frakt-leverans" className="text-slate-500 hover:text-primary text-sm transition-colors">Frakt & Leverans</Link>
              </li>
              <li>
                <Link to="/sida/returer" className="text-slate-500 hover:text-primary text-sm transition-colors">Returer</Link>
              </li>
              <li>
                <Link to="/sida/kontakt" className="text-slate-500 hover:text-primary text-sm transition-colors">Kontakt</Link>
              </li>
            </ul>
          </div>

          {/* Info */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-4">Information</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/sida/om-oss" className="text-slate-500 hover:text-primary text-sm transition-colors">Om oss</Link>
              </li>
              <li>
                <Link to="/sida/integritetspolicy" className="text-slate-500 hover:text-primary text-sm transition-colors">Integritetspolicy</Link>
              </li>
              <li>
                <Link to="/sida/kopvillkor" className="text-slate-500 hover:text-primary text-sm transition-colors">Köpvillkor</Link>
              </li>
              <li>
                <Link to="/foretag" className="text-slate-500 hover:text-primary text-sm transition-colors">För företag</Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-slate-200 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 text-sm">
            © 2024 Printsout. Alla rättigheter förbehållna.
          </p>
          <div className="flex items-center gap-4">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg" 
              alt="Visa" 
              className="h-6 opacity-50 grayscale hover:opacity-100 hover:grayscale-0 transition-all"
            />
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" 
              alt="Mastercard" 
              className="h-6 opacity-50 grayscale hover:opacity-100 hover:grayscale-0 transition-all"
            />
            <span className="text-slate-400 text-xs px-2 py-1 border border-slate-300 rounded">Swish</span>
            <span className="text-slate-400 text-xs px-2 py-1 border border-slate-300 rounded">Klarna</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
